import * as React from "react";
import {
  getDesktop,
  getDesktopFlowDirection,
  getGap,
  getMobile,
  getMobileFlowDirection,
  getPaddings,
  getRounded,
} from "@/utils/theme-helper";
import { CMSData, CMSSection } from "@/cms/models";
import { ApplicationFraction } from "@/utils/gql/graphql";
import { GetContentComponent } from "@/services/component.service";

export interface FlowComponentProps {
  bgColor: string;
  gap: "xs" | "sm" | "md" | "lg" | "xl";
  items: any[];
  padding: string;
  cmsData: CMSData;
  widths?: string[]; // Optional, used only for horizontal direction
  align: {
    vertical: string;
    horizontal: string;
  };
  index: string;
  app: ApplicationFraction;
  cmsSection: CMSSection<any>;
  direction: "vertical" | "horizontal"; // New prop to define flow direction
  rounded?: string;
  video?: {
    type: string;
    url: string;
  };
}

const FlowComponent: React.FC<{
  config: FlowComponentProps;
}> = ({ config }) => {
  // Determine flex direction and alignment based on direction
  const mobileFlowDirection = getMobileFlowDirection(config.direction);
  const desktopFlowDirection = getDesktopFlowDirection(config.direction);
  // Mobile classes
  const mobileFlexDirection =
    mobileFlowDirection === "vertical" ? "flex-col" : "flex-row";
  const mobileAlignItems =
    mobileFlowDirection === "vertical"
      ? `items-${config.align?.horizontal ?? "center"}`
      : `items-${config.align?.vertical ?? "center"}`;
  const mobileJustifyContent =
    mobileFlowDirection === "vertical"
      ? `justify-${config.align?.vertical ?? "center"}`
      : `justify-${config.align?.horizontal ?? "center"}`;

  // Desktop classes (with md: prefix)
  const desktopFlexDirection =
    desktopFlowDirection === "vertical" ? "md:flex-col" : "md:flex-row";
  const desktopAlignItems =
    desktopFlowDirection === "vertical"
      ? `md:items-${config.align?.horizontal ?? "center"}`
      : `md:items-${config.align?.vertical ?? "center"}`;
  const desktopJustifyContent =
    desktopFlowDirection === "vertical"
      ? `md:justify-${config.align?.vertical ?? "center"}`
      : `md:justify-${config.align?.horizontal ?? "center"}`;

  return (
    <div
      data-type="FlowComponent"
      style={{ backgroundColor: config?.bgColor }}
      className={`relative rounded- flex w-full h-full ${mobileFlexDirection} ${mobileAlignItems} ${mobileJustifyContent} ${desktopFlexDirection} ${desktopAlignItems} ${desktopJustifyContent} ${getPaddings(config.padding)} ${getGap(config.gap)} ${getRounded(config.rounded)} overflow-hidden`}
    >
      {config?.video?.type === "realequity" && (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute -z-10 inset-0 object-cover h-full w-full"
        >
          <source src={config.video.url} type="video/mp4" />
        </video>
      )}
      {config?.video?.type === "youtube" && (
        <iframe
          width="100%"
          height="100%"
          src={config.video.url}
          title="youtube"
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          className="absolute inset-0 w-full h-full -z-10"
        ></iframe>
      )}
      {config?.video?.type === "vimeo" && (
        <iframe
          title="vimeo"
          src={config.video.url}
          width="100%"
          height="100%"
          allow="autoplay; fullscreen; picture-in-picture"
          className="absolute inset-0 w-full h-full -z-10"
        ></iframe>
      )}

      {config.items.map((item, itemIndex) => {
        const key = `flow-content-${config.index}-${itemIndex}`;
        const content = GetContentComponent(
          config.cmsData,
          item,
          key,
          itemIndex,
          config.app
        );

        const mobileW = getMobile("none", item.width);
        const desktopW = getDesktop("none", item.width);
        const mobileH = getMobile("none", item.height);
        const desktopH = getDesktop("none", item.height);
        const mobileMaxH = getMobile("none", item.maxHeight);
        const desktopMaxH = getDesktop("none", item.maxHeight);

        return (
          <div
            data-type="FlowComponent#horizontal"
            key={key}
            className={`${mobileW !== "none" ? "w-[var(--w)]" : ""} ${desktopW !== "none" ? "md:w-[var(--mdw)]" : ""} ${mobileH !== "none" ? "h-[var(--h)]" : ""} ${desktopH !== "none" ? "md:h-[var(--mdh)]" : ""} ${mobileMaxH !== "none" ? "max-h-[var(--max-h)]" : ""} ${desktopMaxH !== "none" ? "md:max-h-[var(--mdmax-h)]" : ""}`}
            style={
              {
                "--w": mobileW,
                "--mdw": desktopW,
                "--h": mobileH,
                "--mdh": desktopH,
                "--max-h": mobileMaxH,
                "--mdmax-h": desktopMaxH,
              } as React.CSSProperties
            }
          >
            {content}
          </div>
        );
      })}
    </div>
  );
};

export default FlowComponent;
